import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import Pagination from '@/components/ui/pagination';
import { Loader2 } from 'lucide-react';
import { userService } from '../../../_services';

// ─── Mobile card ─────────────────────────────────────────────────────────────
function CommissionCard({ row, commission, type, onClick }) {
  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
    >
      <CardContent className="p-4 space-y-3">
        {/* Header row: name + total */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate">{row.distributorName}</p>
            <p className="text-xs text-muted-foreground truncate">{row.emailId}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-xs text-muted-foreground">Total</p>
            <p className="font-bold text-sm">${row.totalCommission?.toFixed(2) || '0.00'}</p>
          </div>
        </div>

        {/* Detail grid */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm border-t pt-3">
          <div>
            <p className="text-xs text-muted-foreground">Commission</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="font-medium">${commission}</span>
              {type && (
                <Badge variant={type === 'Direct' ? 'default' : 'secondary'} className="text-xs font-normal">
                  {type}
                </Badge>
              )}
            </div>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Override</p>
            <p className="font-medium mt-0.5">${row.subDistComm?.toFixed(2) || '0.00'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Secondary Override</p>
            <p className="font-medium mt-0.5">${row.parentSubDistComm?.toFixed(2) || '0.00'}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Helper: compute commission/type for a row ────────────────────────────────
function resolveCommission(row) {
  let commission = '0.00';
  let type = '';
  if (row.directComm != null) {
    if (parseInt(row.directComm.toFixed(2)) === 0) {
      commission = row.tiredComm?.toFixed(2) ?? '0.00';
      type = 'Tiered';
    } else {
      commission = row.directComm.toFixed(2);
      type = 'Direct';
    }
  }
  return { commission, type };
}

// ─── Main table component ─────────────────────────────────────────────────────
function DirectCommissionTable({ searchText, selectedMonth, onRowClick, onLoadingChange }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageOfItems, setPageOfItems] = useState([]);

  const updateLoading = (val) => {
    setLoading(val);
    onLoadingChange?.(val);
  };

  useEffect(() => {
    if (selectedMonth) {
      fetchData();
    }
  }, [selectedMonth]);

  const fetchData = async () => {
    updateLoading(true);
    try {
      const response = await userService.SecondaryCommTracking_Tier_NonTier(selectedMonth);
      setData(response || []);
    } catch (error) {
      console.error('Error fetching commission data:', error);
      setData([]);
    } finally {
      updateLoading(false);
    }
  };

  const filteredData = searchText
    ? data.filter(item =>
        item.distributorName?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.emailId?.toLowerCase().includes(searchText.toLowerCase())
      )
    : data;

  const displayData = searchText ? filteredData : pageOfItems;

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── Desktop table (md+) ───────────────────────────────────────────── */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Distributor Name</TableHead>
                  <TableHead>Email Address</TableHead>
                  <TableHead>Commission</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Override</TableHead>
                  <TableHead>Secondary Override</TableHead>
                  <TableHead>Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayData.length > 0 ? (
                  displayData.map((row, index) => {
                    const { commission, type } = resolveCommission(row);
                    return (
                      <TableRow
                        key={row.distributor_UserId || index}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => onRowClick(row, selectedMonth)}
                      >
                        <TableCell>{row.distributorName}</TableCell>
                        <TableCell>{row.emailId}</TableCell>
                        <TableCell>${commission}</TableCell>
                        <TableCell>
                          {type && (
                            <Badge variant={type === 'Direct' ? 'default' : 'secondary'} className="font-normal">
                              {type}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>${row.subDistComm?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell>${row.parentSubDistComm?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell className="font-semibold">
                          ${row.totalCommission?.toFixed(2) || '0.00'}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      No records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {!searchText && filteredData.length > 0 && (
            <div className="p-4 border-t">
              <Pagination
                items={filteredData}
                onChangePage={setPageOfItems}
                searchlength={searchText}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Mobile cards (<md) ────────────────────────────────────────────── */}
      <div className="md:hidden space-y-3">
        {!searchText && filteredData.length > 0 && (
          <Pagination
            items={filteredData}
            onChangePage={setPageOfItems}
            searchlength={searchText}
            stickyTopOnMobile
            className="px-2 py-2"
          />
        )}

        {displayData.length > 0 ? (
          displayData.map((row, index) => {
            const { commission, type } = resolveCommission(row);
            return (
              <CommissionCard
                key={row.distributor_UserId || index}
                row={row}
                commission={commission}
                type={type}
                onClick={() => onRowClick(row, selectedMonth)}
              />
            );
          })
        ) : (
          <Card>
            <CardContent className="text-center p-4 py-8 sm:p-6 sm:py-8 text-muted-foreground">
              No records found
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default DirectCommissionTable;
